// ─────────────────────────────────────────────────────────────────
// Glass-i — Chart of Accounts classification helpers (Stage 6B.9B).
//
// Pure-JS module (no JSX, no axios) so node:test can import it from a
// sibling `__tests__/` directory without a JSX loader. JSX components
// (LedgerModal, LedgerAccountsPage, ContraVoucherModal) re-export
// these constants + helpers verbatim.
//
// Lockstep with backend `accounts.constants.js`:
//   ACCOUNT_SUBTYPES_BY_TYPE
//   LEDGER_ACCOUNT_SUBTYPES
//   ACCOUNT_SUBTYPE_LABELS
//   CANONICAL_ACCOUNT_GROUPS_BY_TYPE
//   isValidSubtypeForType
//   accountTypeFromSubtype
//
// When the backend list changes, this file must be updated in the
// same commit. The frontend pure-JS test asserts size + membership so
// a drift between the two lists fails fast in CI.
// ─────────────────────────────────────────────────────────────────

export const ACCOUNT_TYPES = Object.freeze([
  'asset', 'liability', 'equity', 'income', 'expense'
]);

export const ACCOUNT_SUBTYPES_BY_TYPE = Object.freeze({
  asset: Object.freeze([
    'bank', 'cash', 'sundry_debtor', 'loans_advances_asset',
    'deposits_asset', 'fixed_asset', 'investment',
    'stock_in_hand', 'current_asset', 'other_asset'
  ]),
  liability: Object.freeze([
    'sundry_creditor', 'duty_or_tax', 'current_liability',
    'loan_payable', 'secured_loan', 'unsecured_loan',
    'provision', 'other_liability'
  ]),
  equity: Object.freeze([
    'capital', 'reserves_surplus', 'drawings'
  ]),
  income: Object.freeze([
    'sales', 'direct_income', 'indirect_income', 'other_income'
  ]),
  expense: Object.freeze([
    'purchase', 'direct_expense', 'indirect_expense',
    'depreciation', 'finance_charge'
  ])
});

export const LEDGER_ACCOUNT_SUBTYPES = Object.freeze(
  Object.values(ACCOUNT_SUBTYPES_BY_TYPE).flat());

export const ACCOUNT_SUBTYPE_LABELS = Object.freeze({
  bank:                  'Bank Account',
  cash:                  'Cash-in-Hand',
  sundry_debtor:         'Sundry Debtor',
  loans_advances_asset:  'Loans & Advances (Asset)',
  deposits_asset:        'Deposits (Asset)',
  fixed_asset:           'Fixed Asset',
  investment:            'Investment',
  stock_in_hand:         'Stock-in-Hand',
  current_asset:         'Current Asset',
  other_asset:           'Other Asset',
  sundry_creditor:       'Sundry Creditor',
  duty_or_tax:           'Duty / Tax Payable',
  current_liability:     'Current Liability',
  loan_payable:          'Loan Payable',
  secured_loan:          'Secured Loan',
  unsecured_loan:        'Unsecured Loan',
  provision:             'Provision',
  other_liability:       'Other Liability',
  capital:               'Capital Account',
  reserves_surplus:      'Reserves & Surplus',
  drawings:              'Drawings',
  sales:                 'Sales Account',
  direct_income:         'Direct Income',
  indirect_income:       'Indirect Income',
  other_income:          'Other Income',
  purchase:              'Purchase Account',
  direct_expense:        'Direct Expense',
  indirect_expense:      'Indirect Expense',
  depreciation:          'Depreciation',
  finance_charge:        'Finance Charge'
});

export const CANONICAL_ACCOUNT_GROUPS_BY_TYPE = Object.freeze({
  asset: Object.freeze([
    { group: 'Bank Accounts',          subtype: 'bank' },
    { group: 'Cash-in-Hand',           subtype: 'cash' },
    { group: 'Sundry Debtors',         subtype: 'sundry_debtor' },
    { group: 'Loans & Advances (Asset)', subtype: 'loans_advances_asset' },
    { group: 'Deposits (Asset)',       subtype: 'deposits_asset' },
    { group: 'Fixed Assets',           subtype: 'fixed_asset' },
    { group: 'Investments',            subtype: 'investment' },
    { group: 'Stock-in-Hand',          subtype: 'stock_in_hand' },
    { group: 'Current Assets',         subtype: 'current_asset' },
    { group: 'Other Assets',           subtype: 'other_asset' }
  ]),
  liability: Object.freeze([
    { group: 'Sundry Creditors',    subtype: 'sundry_creditor' },
    { group: 'Duties & Taxes',      subtype: 'duty_or_tax' },
    { group: 'Current Liabilities', subtype: 'current_liability' },
    { group: 'Loans Payable',       subtype: 'loan_payable' },
    { group: 'Secured Loans',       subtype: 'secured_loan' },
    { group: 'Unsecured Loans',     subtype: 'unsecured_loan' },
    { group: 'Provisions',          subtype: 'provision' },
    { group: 'Other Liabilities',   subtype: 'other_liability' }
  ]),
  equity: Object.freeze([
    { group: 'Capital Account',    subtype: 'capital' },
    { group: 'Reserves & Surplus', subtype: 'reserves_surplus' },
    { group: 'Drawings',           subtype: 'drawings' }
  ]),
  income: Object.freeze([
    { group: 'Sales Accounts',   subtype: 'sales' },
    { group: 'Direct Income',    subtype: 'direct_income' },
    { group: 'Indirect Income',  subtype: 'indirect_income' },
    { group: 'Other Income',     subtype: 'other_income' }
  ]),
  expense: Object.freeze([
    { group: 'Purchase Accounts',  subtype: 'purchase' },
    { group: 'Direct Expenses',    subtype: 'direct_expense' },
    { group: 'Indirect Expenses',  subtype: 'indirect_expense' },
    { group: 'Depreciation',       subtype: 'depreciation' },
    { group: 'Finance Charges',    subtype: 'finance_charge' }
  ])
});

// ── Helpers ────────────────────────────────────────────────────
export function isValidSubtypeForType(accountType, accountSubtype) {
  if (accountSubtype === null || accountSubtype === undefined || accountSubtype === '') {
    return true;
  }
  const list = ACCOUNT_SUBTYPES_BY_TYPE[accountType];
  return Array.isArray(list) && list.includes(accountSubtype);
}

export function accountTypeFromSubtype(accountSubtype) {
  for (const [t, subs] of Object.entries(ACCOUNT_SUBTYPES_BY_TYPE)) {
    if (subs.includes(accountSubtype)) return t;
  }
  return null;
}

// Suggest the subtype that pairs with a chosen canonical group, for
// auto-fill in the Ledger modal. Returns the matching subtype if
// `group` matches a canonical group for the chosen `type`; returns
// null otherwise (operator typed a custom group string).
export function subtypeForCanonicalGroup(accountType, accountGroup) {
  const groups = CANONICAL_ACCOUNT_GROUPS_BY_TYPE[accountType];
  if (!Array.isArray(groups) || !accountGroup) return null;
  const trimmed = String(accountGroup).trim().toLowerCase();
  const match = groups.find((g) => g.group.toLowerCase() === trimmed);
  return match ? match.subtype : null;
}

// Reverse pair — the canonical group for a chosen subtype, used as a
// hint when the operator picks subtype first in the modal.
export function canonicalGroupForSubtype(accountType, accountSubtype) {
  const groups = CANONICAL_ACCOUNT_GROUPS_BY_TYPE[accountType];
  if (!Array.isArray(groups) || !accountSubtype) return null;
  const match = groups.find((g) => g.subtype === accountSubtype);
  return match ? match.group : null;
}

// Subtype options for a given account_type, in canonical order.
// Each option is { value, label, group } for direct AntD Select use.
export function subtypeOptionsForType(accountType) {
  const groups = CANONICAL_ACCOUNT_GROUPS_BY_TYPE[accountType];
  if (!Array.isArray(groups)) return [];
  return groups.map((g) => ({
    value: g.subtype,
    label: ACCOUNT_SUBTYPE_LABELS[g.subtype] || g.subtype,
    group: g.group
  }));
}

// Canonical group options for a given account_type, in canonical order.
export function groupOptionsForType(accountType) {
  const groups = CANONICAL_ACCOUNT_GROUPS_BY_TYPE[accountType];
  if (!Array.isArray(groups)) return [];
  return groups.map((g) => ({
    value: g.group, label: g.group, subtype: g.subtype
  }));
}

// Compact colour scheme for the subtype tag in the grid. Reuses the
// asset/liability/equity/income/expense colour palette so a quick
// glance at the column makes the type clear.
export const SUBTYPE_TAG_COLOR = Object.freeze({
  asset:     'green',
  liability: 'red',
  equity:    'gold',
  income:    'blue',
  expense:   'orange'
});
